// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the body of an Agents review card (docs/konvo-agents/DESIGN.md): which machine,
// project, thread and model, then the full prompt, the exact command the agent asks to run, or
// the answers to its questions, the questions it dismisses, or who its question goes to. Nothing is clipped: long text scrolls inside the card, because
// the user confirms what they read. Editing changes the prompt, the model, a new thread's title
// and plan mode, and an ask's note; where it goes stays as the Assistant resolved it.

import {Routes} from '@app/app/Routes';
import {AgentModelPicker, effortLabel, serviceTierLabel} from '@app/features/konvo_agents/components/AgentModelPicker';
import Agents from '@app/features/konvo_agents/state/Agents';
import {modelFor, threadModelChoice} from '@app/features/konvo_agents/state/AgentsModel';
import styles from '@app/features/konvo_assistant/components/AssistantActionCard.module.css';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {ArrowSquareOutIcon} from '@app/features/ui/icons/Icons';
import {AGENT_ASK_NOTE_MAX, type AgentDecision} from '@fluxer/schema/src/domains/konvo/Agents';
import type {AssistantActionDraft} from '@fluxer/schema/src/domains/konvo/AssistantActionDraft';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect} from 'react';

export type AgentActionDraft = Extract<
	AssistantActionDraft,
	{
		kind:
			| 'agent_prompt'
			| 'agent_start'
			| 'agent_stop'
			| 'agent_approval'
			| 'agent_answer'
			| 'agent_dismiss'
			| 'agent_settle'
			| 'agent_rename'
			| 'agent_merge'
			| 'agent_ask';
	}
>;

export function isAgentDraft(draft: AssistantActionDraft | null | undefined): draft is AgentActionDraft {
	return draft?.kind.startsWith('agent_') === true;
}

const KIND = msg({message: 'Agent', comment: 'Assistant review card: kind badge for a coding-agent action.'});
const SEND_PROMPT = msg({message: 'Send prompt', comment: 'Agent review card: confirm button that sends a prompt.'});
const START_THREAD = msg({message: 'Start thread', comment: 'Agent review card: confirm button that starts a thread.'});
const SEND_ANSWERS = msg({
	message: 'Send answers',
	comment: "Agent review card: confirm button that answers the agent's questions.",
});
const DISMISS_QUESTION = msg({
	message: 'Dismiss question',
	comment: "Agent review card: confirm button that closes the agent's question without answering it.",
});
const DISMISS_STOPS = msg({
	message: 'The agent is waiting on this answer, so this stops its turn.',
	comment: "Agent review card: what dismissing the agent's question does when the agent's turn is paused on it.",
});
const DISMISS_CLOSES = msg({
	message: 'Closes the question without an answer. The agent is not told.',
	comment: "Agent review card: what dismissing the agent's question does when the agent has already moved on.",
});
const STOP_THREAD = msg({
	message: 'Stop thread',
	comment: 'Agent review card: confirm button that stops a running turn.',
});
const DECISIONS: Record<AgentDecision, ReturnType<typeof msg>> = {
	accept: msg({message: 'Allow', comment: 'Agent review card: allow the command the agent asks to run, once.'}),
	acceptForSession: msg({
		message: 'Allow for this session',
		comment: 'Agent review card: allow the command and similar ones for the rest of the agent session.',
	}),
	decline: msg({message: 'Deny', comment: 'Agent review card: refuse the command the agent asks to run.'}),
	cancel: msg({
		message: 'Deny and stop',
		comment: 'Agent review card: refuse the command and stop the agent turn.',
	}),
};
const NEW_THREAD = msg({message: 'New thread', comment: 'Agent review card: title when the card starts a new thread.'});
const IN_WORKTREE = msg({
	message: 'In a new worktree',
	comment: 'Agent review card: the new thread runs in its own git worktree.',
});
const PROMPT = msg({message: 'Prompt', comment: 'Agent review card: label above the full prompt text.'});
const ANSWER = msg({
	message: 'Answer: {answer}',
	comment: 'Agent review card: how the request will be answered, e.g. "Answer: Allow".',
});
const ASKS_TO_RUN = msg({
	message: 'The agent asks to run ({kind})',
	comment: 'Agent review card: label above the exact command; {kind} is the request type, e.g. "command".',
});
const STOPS = msg({
	message: 'Stops the turn that is running now.',
	comment: 'Agent review card: what confirming a stop does.',
});
const THREAD_TITLE = msg({message: 'Thread title', comment: 'Agent review card: edit field for a new thread title.'});
const MODEL_LINE = msg({
	message: 'Model: {model}',
	comment: 'Agent review card: the model the agent runs on, e.g. "Model: GPT-6.1-Sol (Codex), Low".',
});
const MODEL_DEFAULT = msg({
	message: 'Model: {model}, the project default',
	comment: 'Agent review card: the thread runs on the model its project uses, e.g. "Claude Opus 5.5 (Claude)".',
});
const MODEL_SWITCH = msg({
	message: 'Switches to {model}',
	comment:
		'Agent review card: the prompt moves the thread to another model, e.g. "Switches to Claude Opus 5.5 (Claude)".',
});
const ANSWERS = msg({
	message: 'Answers',
	comment: "Agent review card: label above the answers to the agent's questions.",
});
const OPEN_THREAD = msg({message: 'Open thread', comment: 'Agent review card: opens the thread on the Agents page.'});
const SETTLE = msg({
	message: 'Settle',
	comment: 'Agent review card: confirm button that files the thread under Settled.',
});
const UNSETTLE = msg({
	message: 'Bring back',
	comment: 'Agent review card: confirm button that brings a settled thread back to the list.',
});
const RENAME = msg({message: 'Rename', comment: 'Agent review card: confirm button that renames the thread.'});
const MERGE = msg({message: 'Merge', comment: 'Agent review card: confirm button that merges the pull request.'});
const ASK_TO_MERGE = msg({
	message: 'Ask to merge',
	comment: 'Agent review card: confirm button that asks the people who may merge the pull request.',
});
const SETTLES = msg({
	message: 'Files it under Settled. You can bring it back any time.',
	comment: 'Agent review card: what confirming a settle does.',
});
const UNSETTLES = msg({
	message: 'Brings it back from Settled.',
	comment: 'Agent review card: what confirming an un-settle does.',
});
const NEW_TITLE = msg({
	message: 'New title: {title}',
	comment: 'Agent review card: the title the thread gets, e.g. "New title: Mail swipe tray".',
});
const BRANCH_WORKTREE = msg({
	message: 'In a new worktree from {branch}',
	comment: 'Agent review card: the new thread works in its own worktree based on this branch.',
});
const BRANCH_SWITCH = msg({
	message: 'On {branch} (the checkout switches to it first)',
	comment: "Agent review card: the project's checkout moves to this branch before the new thread starts.",
});
const MERGE_CHECKS = msg({
	message: 'Checks: {checks}',
	comment: 'Agent review card: how the pull request\'s checks stand, e.g. "Checks: 8 of 8 checks passed".',
});
const MERGES_INTO = msg({
	message: 'Squash-merges it into {base} as you, at the commit you were told about.',
	comment: 'Agent review card: what confirming a merge does; {base} is the base branch, e.g. main.',
});
const MERGE_RELEASES = msg({
	message: 'Then releases: {release}',
	comment: 'Agent review card: what the merge starts afterwards, e.g. "Then releases: Web, API".',
});
const ASK_PERSON = msg({
	message: 'Ask {name}',
	comment: 'Agent review card: confirm button that sends the agent\'s question to a colleague, e.g. "Ask Lerato".',
});
const ASKS_PERSON = msg({
	message: 'Asks {name} in your chat with them. Their answer goes to the agent.',
	comment: "Agent review card: what confirming sends: the agent's question goes into the user's DM with a colleague.",
});
const QUESTION_LABEL = msg({
	message: 'Question',
	comment: "Agent review card: label above the agent's question a colleague is asked.",
});
const QUESTIONS_LABEL = msg({
	message: 'Questions',
	comment: "Agent review card: label above the agent's questions a colleague is asked.",
});
const NOTE_FIRST = msg({
	message: 'Your note first',
	comment: 'Agent review card: label above the note that goes first in the chat with the colleague.',
});
const NOTE_FIELD = msg({
	message: 'Note (optional)',
	comment: 'Agent review card: edit field for the note sent before the agent question to a colleague.',
});
const ASKS_TO_MERGE = msg({
	message: 'Asks the people who may merge in this repository to merge it.',
	comment: 'Agent review card: what confirming Ask to merge does.',
});

export function agentKindLabel(i18n: I18n): string {
	return i18n._(KIND);
}

export function agentConfirmLabel(i18n: I18n, draft: AgentActionDraft): string {
	if (draft.kind === 'agent_prompt') return i18n._(SEND_PROMPT);
	if (draft.kind === 'agent_start') return i18n._(START_THREAD);
	if (draft.kind === 'agent_stop') return i18n._(STOP_THREAD);
	if (draft.kind === 'agent_answer') return i18n._(SEND_ANSWERS);
	if (draft.kind === 'agent_dismiss') return i18n._(DISMISS_QUESTION);
	if (draft.kind === 'agent_settle') return i18n._(draft.settled ? SETTLE : UNSETTLE);
	if (draft.kind === 'agent_rename') return i18n._(RENAME);
	if (draft.kind === 'agent_merge') return i18n._(draft.ask ? ASK_TO_MERGE : MERGE);
	if (draft.kind === 'agent_ask') return i18n._(ASK_PERSON, {name: firstName(draft.personName)});
	return i18n._(DECISIONS[draft.decision]);
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

type DraftModel = Extract<AgentActionDraft, {kind: 'agent_start'}>['model'];

/** "GPT-6.1-Sol (Codex), Low, Fast". */
function modelText(i18n: I18n, model: NonNullable<DraftModel>): string {
	return [
		`${model.name} (${model.providerName})`,
		...(model.effort ? [effortLabel(i18n, model.effort)] : []),
		...(model.serviceTier ? [serviceTierLabel(i18n, model.serviceTier)] : []),
	].join(', ');
}

function ModelLine({draft}: {draft: Extract<AgentActionDraft, {kind: 'agent_prompt' | 'agent_start'}>}) {
	const {i18n} = useLingui();
	if (draft.model)
		return (
			<span className={styles.meta} data-flx="konvo-assistant.action-card.agent-model">
				{draft.kind === 'agent_prompt'
					? i18n._(MODEL_SWITCH, {model: modelText(i18n, draft.model)})
					: i18n._(MODEL_LINE, {model: modelText(i18n, draft.model)})}
			</span>
		);
	if (draft.kind === 'agent_start' && draft.defaultModel)
		return (
			<span className={styles.meta} data-flx="konvo-assistant.action-card.agent-model">
				{i18n._(MODEL_DEFAULT, {model: draft.defaultModel})}
			</span>
		);
	return null;
}

export function AgentActionSummary({draft}: {draft: AgentActionDraft}) {
	const {i18n} = useLingui();
	if (draft.kind === 'agent_merge') return <MergeSummary draft={draft} />;
	const title = draft.kind === 'agent_start' ? draft.title.trim() || i18n._(NEW_THREAD) : draft.threadTitle;
	const place = [draft.machineName, draft.projectTitle].filter(Boolean).join(' · ');
	return (
		<>
			<span className={styles.title}>{title}</span>
			<span className={styles.meta}>
				{place}
				{draft.kind === 'agent_start' && draft.worktree ? ` · ${i18n._(IN_WORKTREE)}` : ''}
			</span>
			{draft.kind === 'agent_start' && draft.branch && (
				<span className={styles.meta} data-flx="konvo-assistant.action-card.agent-branch">
					{i18n._(draft.worktree ? BRANCH_WORKTREE : BRANCH_SWITCH, {branch: draft.branch})}
				</span>
			)}
			{(draft.kind === 'agent_prompt' || draft.kind === 'agent_start') && (
				<>
					<ModelLine draft={draft} />
					<span className={styles.agentLabel}>{i18n._(PROMPT)}</span>
					<span className={styles.agentText} data-flx="konvo-assistant.action-card.agent-prompt">
						{draft.prompt}
					</span>
				</>
			)}
			{draft.kind === 'agent_stop' && <span className={styles.meta}>{i18n._(STOPS)}</span>}
			{draft.kind === 'agent_settle' && (
				<span className={styles.meta}>{i18n._(draft.settled ? SETTLES : UNSETTLES)}</span>
			)}
			{draft.kind === 'agent_rename' && (
				<span className={styles.agentAnswer} data-flx="konvo-assistant.action-card.agent-title">
					{i18n._(NEW_TITLE, {title: draft.title})}
				</span>
			)}
			{draft.kind === 'agent_ask' && (
				<>
					<span className={styles.agentAnswer} data-flx="konvo-assistant.action-card.agent-ask-person">
						{i18n._(ASKS_PERSON, {name: draft.personName})}
					</span>
					<span className={styles.agentLabel}>
						{i18n._(draft.questions.length === 1 ? QUESTION_LABEL : QUESTIONS_LABEL)}
					</span>
					<span className={styles.agentText} data-flx="konvo-assistant.action-card.agent-ask-questions">
						{draft.questions.map((question, index) => (
							<span key={index} className={styles.agentQa}>
								<span className={styles.agentQuestion}>{question}</span>
							</span>
						))}
					</span>
					{draft.note && (
						<>
							<span className={styles.agentLabel}>{i18n._(NOTE_FIRST)}</span>
							<span className={styles.agentText} data-flx="konvo-assistant.action-card.agent-ask-note">
								{draft.note}
							</span>
						</>
					)}
				</>
			)}
			{draft.kind === 'agent_dismiss' && (
				<>
					<span className={styles.agentLabel}>
						{i18n._(draft.questions.length === 1 ? QUESTION_LABEL : QUESTIONS_LABEL)}
					</span>
					<span className={styles.agentText} data-flx="konvo-assistant.action-card.agent-dismiss-questions">
						{draft.questions.map((question, index) => (
							<span key={index} className={styles.agentQa}>
								<span className={styles.agentQuestion}>{question}</span>
							</span>
						))}
					</span>
					<span className={styles.meta} data-flx="konvo-assistant.action-card.agent-dismiss-effect">
						{i18n._(draft.stops ? DISMISS_STOPS : DISMISS_CLOSES)}
					</span>
				</>
			)}
			{draft.kind === 'agent_answer' && (
				<>
					<span className={styles.agentLabel}>{i18n._(ANSWERS)}</span>
					<span className={styles.agentText} data-flx="konvo-assistant.action-card.agent-answers">
						{draft.lines.map((line, index) => (
							<span key={index} className={styles.agentQa}>
								<span className={styles.agentQuestion}>{line.question}</span>
								<span className={styles.agentAnswer}>{line.answer}</span>
							</span>
						))}
					</span>
				</>
			)}
			{draft.kind === 'agent_approval' && (
				<>
					<span className={styles.agentAnswer}>{i18n._(ANSWER, {answer: i18n._(DECISIONS[draft.decision])})}</span>
					<span className={styles.agentLabel}>{i18n._(ASKS_TO_RUN, {kind: draft.requestKind})}</span>
					<pre className={styles.agentCode} data-flx="konvo-assistant.action-card.agent-detail">
						{draft.detail}
					</pre>
				</>
			)}
		</>
	);
}

/** A merge card: the pull request, the thread it came from, its checks and what merging releases. */
function MergeSummary({draft}: {draft: Extract<AgentActionDraft, {kind: 'agent_merge'}>}) {
	const {i18n} = useLingui();
	const place = [draft.repository, draft.threadTitle].filter(Boolean).join(' · ');
	return (
		<>
			<span className={styles.title}>
				#{draft.number} {draft.pullTitle}
			</span>
			<span className={styles.meta}>{place}</span>
			<span className={styles.meta} data-flx="konvo-assistant.action-card.agent-merge-checks">
				{i18n._(MERGE_CHECKS, {checks: draft.checks})}
			</span>
			<span className={styles.agentAnswer} data-flx="konvo-assistant.action-card.agent-merge">
				{draft.ask ? i18n._(ASKS_TO_MERGE) : i18n._(MERGES_INTO, {base: draft.base})}
			</span>
			{!draft.ask && draft.release.length > 0 && (
				<span className={styles.meta}>{i18n._(MERGE_RELEASES, {release: draft.release.join(', ')})}</span>
			)}
		</>
	);
}

/** The thread a prompt goes to, as far as Agents has it: open, else in its machine's list. */
const threadOf = (machineId: string, threadId: string) =>
	Agents.thread(machineId, threadId)?.detail ?? Agents.summary(machineId, threadId);

/** Card edits: the prompt, the model, and a new thread's title and plan mode. Where it goes stays as the Assistant set it. */
export const AgentActionFields = observer(function AgentActionFields({
	draft,
	onChange,
}: {
	draft: AgentActionDraft;
	onChange: (fields: Record<string, unknown>) => void;
}) {
	const {i18n} = useLingui();
	// The model list comes with the machine overview, which the Agents page may not have loaded yet.
	useEffect(() => {
		if (!Agents.machine(draft.machineId)) void Agents.loadOverview();
	}, [draft.machineId]);
	if (draft.kind === 'agent_ask')
		return (
			<label>
				{i18n._(NOTE_FIELD)}
				<textarea
					rows={3}
					maxLength={AGENT_ASK_NOTE_MAX}
					value={draft.note}
					onChange={(e) => onChange({note: e.target.value})}
					data-flx="konvo-assistant.action-card.agent-ask-note-field"
				/>
			</label>
		);
	if (draft.kind !== 'agent_prompt' && draft.kind !== 'agent_start') return null;
	const machine = Agents.machine(draft.machineId);
	const value = draft.model
		? {
				provider: draft.model.provider,
				model: draft.model.model,
				...(draft.model.effort ? {effort: draft.model.effort} : {}),
				...(draft.model.serviceTier ? {serviceTier: draft.model.serviceTier} : {}),
			}
		: null;
	return (
		<>
			{draft.kind === 'agent_start' && (
				<label>
					{i18n._(THREAD_TITLE)}
					<input value={draft.title} maxLength={120} onChange={(e) => onChange({title: e.target.value})} />
				</label>
			)}
			<AgentModelPicker
				machine={machine}
				projectId={draft.kind === 'agent_start' ? draft.projectId : null}
				value={value}
				defaultName={draft.kind === 'agent_start' ? draft.defaultModel : null}
				current={
					draft.kind === 'agent_prompt' ? threadModelChoice(machine, threadOf(draft.machineId, draft.threadId)) : null
				}
				variant="field"
				onChange={(next) => {
					const model = next ? modelFor(machine, next) : null;
					onChange({
						model:
							next && model
								? {...next, effort: next.effort ?? null, name: model.name, providerName: model.providerName}
								: null,
					});
				}}
				dataFlx="konvo-assistant.action-card.agent"
			/>
			<label>
				{i18n._(PROMPT)}
				<textarea rows={8} value={draft.prompt} onChange={(e) => onChange({prompt: e.target.value})} />
			</label>
		</>
	);
});

/** After Confirm: open the thread (a new one once it started). */
export function AgentActionOpen({draft}: {draft: AgentActionDraft}) {
	const {i18n} = useLingui();
	const threadId = draft.kind === 'agent_start' ? draft.startedThreadId : draft.threadId;
	if (!threadId) return null;
	return (
		<button
			type="button"
			className={styles.button}
			onClick={() => RouterUtils.transitionTo(Routes.agents(draft.machineId, threadId))}
			data-flx="konvo-assistant.action-card.open-agent-thread"
		>
			<ArrowSquareOutIcon size={14} />
			{i18n._(OPEN_THREAD)}
		</button>
	);
}
